import db, { updateSyncStatus } from "../db/database";
import { fetchFromBusinessCentralEspecial } from "../utils/businessCentralUtils";
import { marcaConSolape } from "./margenDeSolape";
import { isOnline } from "./syncService";
import { almacenDeLaCajaActiva } from "./cajaEstadoService";
import { explicarFaltaDeAlmacen } from "./tiendaDeLaCaja";
import { anotarAviso, anotarError, anotarLectura, anotarSinConexion } from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "productos";

const normalizePostingGroup = (value) => (value ?? "").toString().trim().toUpperCase();

const resolveVatPercentByProdPostingGroup = (vatProdPostingGroup, vatGroups) => {
  const normalizedProdGroup = normalizePostingGroup(vatProdPostingGroup);

  if (!normalizedProdGroup) {
    return 0;
  }

  const matchingVatGroups = vatGroups.filter(
    (group) =>
      !group.Blocked && normalizePostingGroup(group.VAT_Prod_Posting_Group) === normalizedProdGroup
  );

  if (matchingVatGroups.length === 0) {
    return 0;
  }
  return matchingVatGroups.reduce((maxVat, group) => {
    const currentVat = Number(group?.VAT_Percent ?? 0);
    return Math.max(currentVat, maxVat);
  }, 0);
};

// ---------------------------------------------------------------------------
// Sync de stock por tienda (importaciones_getproductostock), disparado al
// abrir caja y después de sincronizar una factura. Reemplaza al viejo
// syncProducts()/ListaProductos -- ya no se llama en la sync inicial
// (syncAll), el catálogo se trae por tienda activa, no de forma global.
// ---------------------------------------------------------------------------

const parseSourceCounter = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// El Source_Counter de este endpoint no está ligado al maestro de items sino
// a los movimientos de inventario del item en esa tienda (ver plan), así que
// el cursor se calcula sobre los productos ya guardados para esa tienda
// puntual, no sobre toda la tabla.
const getLastSourceCounterForLocation = async (locationCode) => {
  const localProducts = await db.products.where("locationCode").equals(locationCode).toArray();

  if (localProducts.length === 0) return 0;

  // Con colchón: la marca número `FILAS_DE_SOLAPE`, no la mayor. Ver
  // `margenDeSolape.js`. Acá el colchón se cuenta sobre los productos de esta
  // tienda, que es sobre lo que va el cursor.
  return marcaConSolape(localProducts) ?? 0;
};

const mapStockRowToLocalProduct = (row, vatGroups) => {
  const calculatedVat = resolveVatPercentByProdPostingGroup(row.VAT_Prod_Posting_Group, vatGroups);

  return {
    No: row.Item_No,
    description: row.Item_Description,
    description2: row.Item_Description_2,
    Unit_Price: row.Unit_Price || 0,
    VAT_Prod_Posting_Group: row.VAT_Prod_Posting_Group,
    PorIva: calculatedVat,
    Price_Includes_VAT: !!row.Price_Includes_VAT,
    barcode: row.BarCode || "",
    Type: row.Type,
    Item_Category_Code: row.Item_Category_Code,
    Base_Unit_of_Measure: row.Base_Unit_of_Measure,
    SUNAT_Code: row.SUNAT_Code || "",
    OSCE_Code: row.OSCE_Code || "",
    Blocked: !!row.Blocked,
    locationCode: row.Location_Code,
    Stock_Quantity: row.Stock_Quantity,
    Source_Counter: parseSourceCounter(row.Source_Counter),
    lastSynced: new Date(),
    pendingSync: 0,
    deleted: false,
  };
};

// Borra localmente los productos de tiendas distintas a locationCode. Sólo se
// llama después de un fetch exitoso (ver syncProductsStockByLocation), para
// no perder el último snapshot bueno de una tienda si el nuevo fetch falla
// por falta de internet.
const pruneOtherLocations = async (locationCode) => {
  await db.products.filter((p) => !!p.locationCode && p.locationCode !== locationCode).delete();
};

/**
 * Borra localmente los productos de una tienda puntual. Se llama al cerrar
 * caja (cierreDeCajaService.js:cerrarAperturaDelCierre) para no dejar
 * Existencias/Unidad desactualizados de la tienda que se acaba de dejar,
 * mientras no haya una caja abierta que los reemplace.
 *
 * Es seguro borrar todo: `getLastSourceCounterForLocation` deriva el
 * checkpoint de sincronización incremental del propio `Source_Counter` más
 * alto entre los productos YA guardados de esa tienda (línea ~45-50) -- no es
 * un valor aparte. Sin productos locales, el próximo `syncProductsStockByLocation`
 * de esa tienda arranca con sourceCounter 0 y BC devuelve el catálogo
 * completo de nuevo (no un delta a medias).
 * @param {string} locationCode
 */
/**
 * El catálogo de productos cambió.
 *
 * Quien lo tiene cargado no se entera solo: el formulario de factura lee los
 * productos **una vez al montarse** y se queda con esa lista. Si el catálogo
 * llega después --al abrir caja, que es cuando se sincroniza el stock de la
 * tienda-- el desplegable se queda vacío aunque la pantalla de Productos los
 * muestre, y sólo se arregla saliendo y volviendo a entrar.
 *
 * Es el mismo aviso del navegador que ya usan la caja activa y los rechazos de
 * sincronización. Se prefiere a un `useLiveQuery` de Dexie porque no añade una
 * dependencia nueva y porque el proyecto ya tiene este camino andado.
 */
export const EVENTO_PRODUCTOS = "productosCambiaron";

const avisarCambioDeProductos = () => {
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_PRODUCTOS));
  } catch (error) {
    // Un aviso que no se puede emitir no puede tumbar la sincronización que lo
    // provocó: los productos ya están guardados, esto sólo refresca lo que se ve.
    console.warn("No se pudo avisar el cambio de productos:", error);
  }
};

export const clearProductsForLocation = async (locationCode) => {
  if (!locationCode) return;
  await db.products.filter((p) => p.locationCode === locationCode).delete();
  avisarCambioDeProductos();
};

const saveStockBatch = async (rows, locationCode) => {
  const vatGroups = await db.grupoIVA.toArray();

  await db.transaction("rw", db.products, async () => {
    for (const row of rows) {
      const data = mapStockRowToLocalProduct(row, vatGroups);

      const existing = await db.products
        .where("[No+locationCode]")
        .equals([data.No, locationCode])
        .first();

      if (existing) {
        await db.products.put({ ...existing, ...data });
      } else {
        await db.products.add(data);
      }
    }
  });
};

/**
 * Sincroniza el stock (y maestro de producto) de una tienda puntual contra
 * importaciones_getproductostock. Se llama al abrir caja y después de
 * sincronizar una factura -- siempre en background, nunca debe bloquear esos
 * flujos (la primera sync de una tienda puede tardar, no hay paginado del
 * lado de backend).
 * @param {string} locationCode
 * @returns {Promise<boolean>}
 */
export const syncProductsStockByLocation = async (locationCode) => {
  if (!locationCode) {
    anotarAviso(ENTIDAD, "no se indicó almacén, no hay nada que leer");
    return false;
  }

  if (!isOnline()) {
    anotarSinConexion(ENTIDAD);
    return false;
  }

  try {
    const lastSourceCounter = await getLastSourceCounterForLocation(locationCode);

    const response = await fetchFromBusinessCentralEspecial("Importaciones_getproductostock", {
      itemNo: "",
      locationCode,
      sourceCounter: lastSourceCounter,
    });

    if (!response) {
      anotarAviso(
        ENTIDAD,
        `Business Central no devolvió respuesta para el almacén ${locationCode}`
      );
      return false;
    }

    const stockData = JSON.parse(response);
    const rows = stockData?.Product_Stock || [];

    // La consulta es incremental: se pide sólo lo posterior al último contador
    // conocido de este almacén. Cero filas significa "no hay novedades", no un
    // fallo -- pero sin decirlo se confunde con que la sincronización no corrió.
    anotarLectura(ENTIDAD, {
      guardados: rows.length,
      incremental: lastSourceCounter > 0,
      marca: lastSourceCounter,
    });

    // Recién acá, con la respuesta ya en mano, podamos otras tiendas --
    // si el fetch hubiera fallado antes, no tocamos lo que ya había.
    await pruneOtherLocations(locationCode);

    if (rows.length > 0) {
      await saveStockBatch(rows, locationCode);
    }

    // Se anota en la bitácora de sincronización. Este camino no pasa por
    // executeWithRetry -- se llama directo al abrir caja, al sincronizar una
    // factura y desde el listado de productos -- así que sin esto la ficha de
    // Configuración seguía diciendo "Sin sincronizar / Último intento: Nunca"
    // por más productos que se hubieran traído.
    await updateSyncStatus("products", true, await db.products.count());
    avisarCambioDeProductos();

    return true;
  } catch (error) {
    anotarError(ENTIDAD, `falló la lectura del almacén ${locationCode}`, error);
    await updateSyncStatus("products", false, 0, error.message);
    return false;
  }
};

/**
 * Resuelve la tienda activa (caja abierta) y sincroniza su stock. Pensada
 * para los puntos de sync manual (botón "Sincronizar productos" y el listado
 * de modelos de Configuración), donde no hay un locationCode a mano de
 * entrada. Tira error si no hay tienda activa, para que el caller lo muestre
 * como cualquier otro error de sync manual.
 * @returns {Promise<boolean>}
 */
export const syncProductsForActiveStore = async () => {
  // El mensaje decía «No hay una caja abierta» en los cuatro casos, y sólo uno
  // de ellos lo era: los otros tres pasan **con una caja abierta delante**, y
  // mandaban a abrir otra --que nacía con el mismo hueco--. Ahora cada motivo
  // dice a dónde ir: a abrir caja, a sincronizar Cajas, a sincronizar Tiendas,
  // o a Business Central.
  const diagnostico = await almacenDeLaCajaActiva();
  const { locationCode } = diagnostico;
  if (!locationCode) {
    const error = new Error(explicarFaltaDeAlmacen(diagnostico));
    error.code = "NO_ACTIVE_LOCATION";
    error.motivo = diagnostico.motivo;
    // No tiene sentido reintentar esto (no es un error transitorio de red,
    // es que falta abrir caja) -- ver executeWithRetry en syncService.js.
    error.nonRetryable = true;
    throw error;
  }
  return syncProductsStockByLocation(locationCode);
};
